import { useEffect, useState } from "react";

import type { AdminDashboardSummaryResponseData, BookingStatus, OrderStatus } from "@chaochud/contracts";

import { PageHeader } from "@/components/admin-ui";
import { adminOpsClient } from "@/lib/api-clients";
import { useAdminAuth } from "@/lib/auth/auth-context";

const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  PENDING_CONFIRMATION: "รอร้านค้ายืนยัน",
  CONFIRMED: "ร้านค้ายืนยันแล้ว",
  PAYMENT_REPORTED: "แจ้งชำระเงินแล้ว",
  PAYMENT_CONFIRMED: "ยืนยันชำระเงินแล้ว",
  RENTING: "อยู่ในช่วงเช่า",
  RETURNING: "กำลังคืนสินค้า",
  RETURNED: "คืนสินค้าแล้ว",
  COMPLETED: "เสร็จสิ้น",
  REJECTED: "ถูกปฏิเสธ",
  EXPIRED: "หมดเวลา",
  CANCELED: "ยกเลิกแล้ว",
};

const BOOKING_STATUS_TONE_CLASS: Record<BookingStatus, string> = {
  PENDING_CONFIRMATION: "bg-warning-text",
  CONFIRMED: "bg-success-text",
  PAYMENT_REPORTED: "bg-warning-text",
  PAYMENT_CONFIRMED: "bg-success-text",
  RENTING: "bg-success-text",
  RETURNING: "bg-warning-text",
  RETURNED: "bg-neutral-text",
  COMPLETED: "bg-neutral-text",
  REJECTED: "bg-error-text",
  EXPIRED: "bg-neutral-text",
  CANCELED: "bg-error-text",
};

// Mirrors apps/web's order-status.ts labels so admin and customer/seller UI
// use the same Thai vocabulary for a given status.
const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  PENDING_PAYMENT: "รอชำระเงิน",
  PAYMENT_SUBMITTED: "รอร้านตรวจสอบการชำระเงิน",
  PAID: "ชำระเงินแล้ว",
  PACKING: "กำลังเตรียมจัดส่ง",
  SHIPPED: "จัดส่งแล้ว",
  COMPLETED: "เสร็จสิ้น",
  CANCELED: "ยกเลิกแล้ว",
  STOCK_UNAVAILABLE: "สินค้าหมด (รอคืนเงิน)",
};

const ORDER_STATUS_TONE_CLASS: Record<OrderStatus, string> = {
  PENDING_PAYMENT: "bg-warning-text",
  PAYMENT_SUBMITTED: "bg-warning-text",
  PAID: "bg-success-text",
  PACKING: "bg-warning-text",
  SHIPPED: "bg-success-text",
  COMPLETED: "bg-neutral-text",
  CANCELED: "bg-error-text",
  STOCK_UNAVAILABLE: "bg-error-text",
};

function formatDayLabel(dateKey: string | undefined): string {
  if (!dateKey) return "";
  return new Date(`${dateKey}T00:00:00Z`).toLocaleDateString("th-TH", { day: "numeric", month: "short" });
}

function formatThb(value: number): string {
  return `฿${value.toLocaleString("th-TH", { maximumFractionDigits: 0 })}`;
}

/** `GET /admin/dashboard/summary` — platform-wide KPI cards + chart data (docs/roadmap.md §13). */
export function DashboardPage() {
  const { admin } = useAdminAuth();
  const [summary, setSummary] = useState<AdminDashboardSummaryResponseData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    adminOpsClient
      .getDashboardSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "โหลดข้อมูลแดชบอร์ดไม่สำเร็จ");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <PageHeader title="ภาพรวมแพลตฟอร์ม" />
      <div className="mb-5 space-y-1 rounded-xl border border-slate-line bg-slate-surface p-5 text-sm">
        <p className="text-ink">ยินดีต้อนรับ {admin?.displayName ?? admin?.email}</p>
        <p className="text-muted">บทบาท: {admin?.roles.join(", ") || "-"}</p>
      </div>

      {isLoading && <p className="text-sm text-muted">กำลังโหลดข้อมูล...</p>}
      {error && <p className="text-sm text-error-text">{error}</p>}

      {summary && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard label="ผู้ใช้ทั้งหมด" value={summary.counts.totalUsers} />
            <KpiCard label="ร้านค้าทั้งหมด" value={summary.counts.totalStores} />
            <KpiCard
              label="ร้านรอตรวจสอบ"
              value={summary.counts.pendingStores}
              tone={summary.counts.pendingStores > 0 ? "warning" : "neutral"}
            />
            <KpiCard label="ร้านที่เปิดขาย" value={summary.counts.activeStores} tone="success" />
            <KpiCard label="การจองทั้งหมด" value={summary.counts.totalBookings} />
            <KpiCard
              label="รอร้านยืนยันการจอง"
              value={summary.counts.pendingBookings}
              tone={summary.counts.pendingBookings > 0 ? "warning" : "neutral"}
            />
            <KpiCard label="คำสั่งซื้อทั้งหมด" value={summary.counts.totalOrders} />
            <KpiCard
              label="รายงานรอตรวจสอบ"
              value={summary.counts.openReports}
              tone={summary.counts.openReports > 0 ? "error" : "neutral"}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <TimeSeriesBarChart
              title="ผู้ใช้สมัครใหม่ (14 วันล่าสุด)"
              points={summary.dailySeries.map((point) => ({ date: point.date, value: point.newUsers }))}
              formatValue={(value) => `${value} คน`}
              barClassName="bg-plum"
            />
            <TimeSeriesBarChart
              title="คำขอจองใหม่ (14 วันล่าสุด)"
              points={summary.dailySeries.map((point) => ({ date: point.date, value: point.newBookings }))}
              formatValue={(value) => `${value} รายการ`}
              barClassName="bg-warning-text"
            />
            <TimeSeriesBarChart
              title="รายได้ที่ยืนยันแล้ว (14 วันล่าสุด)"
              points={summary.dailySeries.map((point) => ({ date: point.date, value: point.revenue }))}
              formatValue={formatThb}
              barClassName="bg-success-text"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <StatusBreakdownChart
              title="สถานะการจองทั้งหมด"
              items={summary.bookingStatusBreakdown}
              labelMap={BOOKING_STATUS_LABEL}
              toneClassMap={BOOKING_STATUS_TONE_CLASS}
            />
            <StatusBreakdownChart
              title="สถานะคำสั่งซื้อทั้งหมด"
              items={summary.orderStatusBreakdown}
              labelMap={ORDER_STATUS_LABEL}
              toneClassMap={ORDER_STATUS_TONE_CLASS}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number;
  tone?: "success" | "warning" | "error" | "neutral";
}) {
  const toneClass =
    tone === "success"
      ? "text-success-text"
      : tone === "warning"
        ? "text-warning-text"
        : tone === "error"
          ? "text-error-text"
          : "text-ink";
  return (
    <div className="rounded-xl border border-slate-line bg-slate-surface p-4">
      <p className="text-[11.5px] font-semibold text-muted">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-bold ${toneClass}`}>{value.toLocaleString("th-TH")}</p>
    </div>
  );
}

/** Simple flex/CSS bar chart — no chart library dependency, bar heights are plain percentages of a fixed-height container. */
function TimeSeriesBarChart({
  title,
  points,
  formatValue,
  barClassName,
}: {
  title: string;
  points: { date: string; value: number }[];
  formatValue: (value: number) => string;
  barClassName: string;
}) {
  const max = Math.max(1, ...points.map((point) => point.value));
  return (
    <div className="rounded-xl border border-slate-line bg-slate-surface p-4.5">
      <p className="mb-3 text-[13px] font-semibold text-ink">{title}</p>
      <div className="flex h-[120px] items-end gap-1">
        {points.map((point) => (
          <div
            key={point.date}
            className="group relative flex-1"
            title={`${formatDayLabel(point.date)}: ${formatValue(point.value)}`}
          >
            <div
              className={`w-full rounded-t transition-opacity group-hover:opacity-80 ${barClassName}`}
              style={{ height: `${Math.max(2, (point.value / max) * 100)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-muted">
        <span>{formatDayLabel(points[0]?.date)}</span>
        <span>{formatDayLabel(points[points.length - 1]?.date)}</span>
      </div>
    </div>
  );
}

/** Horizontal bar breakdown by status — width proportional to the largest bucket, sorted descending, zero-count statuses hidden. */
function StatusBreakdownChart<TStatus extends string>({
  title,
  items,
  labelMap,
  toneClassMap,
}: {
  title: string;
  items: { status: TStatus; count: number }[];
  labelMap: Record<TStatus, string>;
  toneClassMap: Record<TStatus, string>;
}) {
  const visible = items.filter((item) => item.count > 0).sort((a, b) => b.count - a.count);
  const max = Math.max(1, ...visible.map((item) => item.count));
  const total = items.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="rounded-xl border border-slate-line bg-slate-surface p-4.5">
      <p className="mb-3 text-[13px] font-semibold text-ink">
        {title} <span className="font-normal text-muted">(รวม {total.toLocaleString("th-TH")})</span>
      </p>
      {visible.length === 0 ? (
        <p className="text-[12px] text-muted">ยังไม่มีข้อมูล</p>
      ) : (
        <div className="flex flex-col gap-2">
          {visible.map((item) => (
            <div key={item.status} className="flex items-center gap-2">
              <span className="w-[130px] shrink-0 truncate text-[11.5px] text-ink">
                {labelMap[item.status] ?? item.status}
              </span>
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-neutral-bg">
                <div
                  className={`h-full rounded-full ${toneClassMap[item.status] ?? "bg-plum"}`}
                  style={{ width: `${(item.count / max) * 100}%` }}
                />
              </div>
              <span className="w-[36px] shrink-0 text-right text-[11.5px] font-semibold text-ink">{item.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
